import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Scale, RefreshCw, Database, Image as ImageIcon, AlertTriangle, ArrowLeft } from 'lucide-react'
import PageHeader from '../../components/PageHeader'
import DataLoadError from '../../components/DataLoadError'
import { getSmartKraalHealth, getSmartKraalPromotionPreview, getSmartKraalHerdSummary } from '../../services/api'
import { formatEAT } from '../../components/SmartKraalWeighings'

const fmtBytes = (b) => (b >= 1e9 ? `${(b / 1e9).toFixed(1)} GB` : b >= 1e6 ? `${(b / 1e6).toFixed(0)} MB` : `${b} B`)

/**
 * SmartKraal sync health (D11 phase 1): what the daily pull did, how fresh
 * the data set is, per-surface state, media backlog, promotion state and the
 * confidence / match-rate preview used to choose the promotion threshold.
 */
export default function SmartKraalSync({ ranchId }) {
  const health = useQuery({
    queryKey: ['smartkraalHealth', ranchId],
    queryFn: ({ signal }) => getSmartKraalHealth(ranchId, { signal }),
    enabled: !!ranchId,
  })
  const preview = useQuery({
    queryKey: ['smartkraalPreview', ranchId],
    queryFn: ({ signal }) => getSmartKraalPromotionPreview(ranchId, { signal }),
    enabled: !!ranchId,
  })
  const herd = useQuery({
    queryKey: ['smartkraalHerd', ranchId],
    queryFn: ({ signal }) => getSmartKraalHerdSummary(ranchId, { signal }),
    enabled: !!ranchId,
  })

  if (health.error) return <DataLoadError error={health.error} title="Could not load SmartKraal sync health" />
  const h = health.data
  if (!h) return <div className="animate-pulse h-32 card" />
  if (h.configured === false) {
    return (
      <div className="space-y-4">
        <PageHeader title="SmartKraal sync" icon={Scale} subtitle="Not configured for this ranch" />
        <p className="text-earth-600">
          No SmartKraal farm is linked to this ranch. A system administrator links the kraal's farm id
          under Ranch settings; the daily pull starts the next morning.
        </p>
      </div>
    )
  }
  const lastRun = h.runs?.[0]
  const p = preview.data
  const hs = herd.data

  return (
    <div className="space-y-6">
      <Link to="/cattle" className="inline-flex items-center text-earth-500 hover:text-earth-700">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Cattle
      </Link>
      <PageHeader
        title="SmartKraal sync"
        icon={Scale}
        subtitle={`Farm ${h.farm?.farm_id ?? '—'}${h.farm && !h.farm.enabled ? ' (pull disabled)' : ''} · data set as of ${formatEAT(h.last_data_as_of_utc)} · pulled daily from 10:30 EAT, hourly retries to 15:30`}
      />

      {/* Status cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="text-xs text-earth-500 flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Last run</div>
          <div className={`text-lg font-semibold ${lastRun?.status === 'failed' ? 'text-red-700' : 'text-primary-900'}`}>
            {lastRun ? `${lastRun.status}${lastRun.skip_reason ? ` (${lastRun.skip_reason})` : ''}` : '—'}
          </div>
          <div className="text-xs text-earth-500">{lastRun ? formatEAT(lastRun.started_at) : ''}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-earth-500 flex items-center gap-1"><Database className="h-3 w-3" /> Mirror</div>
          <div className="text-lg font-semibold text-primary-900">
            {(h.surfaces || []).reduce((a, s) => a + Number(s.rows || 0), 0).toLocaleString()} rows
          </div>
          <div className="text-xs text-earth-500">{(h.surfaces || []).filter(s => s.has_cursor).length}/{(h.surfaces || []).length} surfaces with a cursor</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-earth-500 flex items-center gap-1"><ImageIcon className="h-3 w-3" /> Media</div>
          <div className="text-lg font-semibold text-primary-900">{h.media?.held ?? 0} files · {fmtBytes(h.media?.bytes || 0)}</div>
          <div className="text-xs text-earth-500">backlog {h.media?.backlog ?? 0} · failed/pending {h.media?.failed_or_pending ?? 0}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-earth-500 flex items-center gap-1"><Scale className="h-3 w-3" /> Weight records</div>
          <div className="text-lg font-semibold text-primary-900">{h.promotion?.live ?? 0} live</div>
          <div className="text-xs text-earth-500">
            {h.settings?.min_confidence_promote == null
              ? 'promotion OFF (threshold not set)'
              : `threshold ≥ ${h.settings.min_confidence_promote} · ${h.promotion?.retracted ?? 0} retracted`}
          </div>
        </div>
      </div>

      {Object.keys(h.open_exceptions || {}).length > 0 && (
        <div className="card p-4 border-amber-300 bg-amber-50 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
          <div className="text-sm text-amber-900">
            Open SmartKraal exceptions:{' '}
            {Object.entries(h.open_exceptions).map(([k, v]) => `${k.replace('smartkraal_', '')} (${v})`).join(', ')}.
            See the assurance register.
          </div>
        </div>
      )}

      {/* Herd card */}
      {hs?.configured && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-medium text-primary-900">Herd at the crush</h3>
            <Link to="/cattle/registration" className="text-sm text-primary-600 hover:text-primary-800">Register tags at the crush →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-earth-500">Last dip day</div>
              <div className="font-semibold">{hs.last_dip_day?.date || '—'}</div>
              <div className="text-earth-600">{hs.last_dip_day ? `${hs.last_dip_day.animals} animals · median ${hs.last_dip_day.median_weight_kg} kg` : ''}</div>
            </div>
            <div>
              <div className="text-earth-500">Tags bound to animals</div>
              <div className="font-semibold">
                {hs.tags ? `${hs.tags.weighed_bound} of ${hs.tags.weighed}` : `${hs.animals_linked} of ${hs.animals_with_c_num}`}
              </div>
              <div className="text-earth-600">
                {hs.tags
                  ? `weighed tags with a WildeBeef animal · ${hs.tags.weighed_unbound} unbound` +
                    (hs.tags.sent_back ? ` · ${hs.tags.sent_back} sent back` : '') +
                    ` · ${hs.animals_linked} of ${hs.animals_with_c_num} farm numbers match`
                  : 'with a WildeBeef animal id on the scale'}
              </div>
            </div>
            <div>
              <div className="text-earth-500">Weekly median (confidence ≥ {hs.min_confidence_display})</div>
              <div className="flex flex-wrap gap-2 mt-1">
                {(hs.weekly || []).map(w => (
                  <span key={w.week_start} className="rounded bg-earth-100 px-2 py-0.5 text-xs">
                    {w.week_start}: {w.median_weight_kg} kg ({w.animals})
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Promotion preview */}
      {p?.configured && (
        <div className="card p-6">
          <h3 className="text-lg font-medium text-primary-900 mb-2">Promotion preview</h3>
          <p className="text-sm text-earth-600 mb-4">
            Best-daily readings that link to an animal on this ranch, by confidence. Match rate: {' '}
            <span className="font-semibold">{p.c_num_match_rate == null ? '—' : `${Math.round(p.c_num_match_rate * 100)}%`}</span>
            {' '}({p.distinct_c_num_linked} of {p.distinct_c_num} animal ids on the scale exist in the register).
            {p.linked_by && (
              <>
                {' '}Linked through our tag bindings: <span className="font-semibold">{p.linked_by.tag}</span>,
                through the farm number only: <span className="font-semibold">{p.linked_by.c_num}</span>.
                {p.unbound_tags > 0 && <> Tags weighed but not yet bound: <span className="font-semibold">{p.unbound_tags}</span>.</>}
                {p.binding_mismatch_rows > 0 && <> Readings where binding and farm number disagree: <span className="font-semibold">{p.binding_mismatch_rows}</span>.</>}
              </>
            )}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <div className="text-earth-500 mb-1">Linked readings at or above a threshold</div>
              <table className="min-w-full">
                <tbody>
                  {Object.entries(p.linked_rows_at_or_above || {}).map(([t, n]) => (
                    <tr key={t} className={String(p.current_threshold) === t ? 'font-semibold' : ''}>
                      <td className="py-0.5 pr-4">≥ {t}</td><td className="py-0.5">{n}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <div className="text-earth-500 mb-1">Why rows do not qualify (threshold 0)</div>
              <table className="min-w-full">
                <tbody>
                  {Object.entries(p.by_state_at_threshold_0 || {}).map(([k, n]) => (
                    <tr key={k}><td className="py-0.5 pr-4">{k.replace(/_/g, ' ')}</td><td className="py-0.5">{n}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Surfaces */}
      <div className="card p-6 overflow-x-auto">
        <h3 className="text-lg font-medium text-primary-900 mb-3">Surfaces</h3>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-earth-500 border-b border-earth-200">
              <th className="py-2 pr-4">Surface</th><th className="py-2 pr-4 text-right">Rows</th>
              <th className="py-2 pr-4">Last pull</th><th className="py-2 pr-4">+ / ~ / −</th><th className="py-2 pr-4">Error</th>
            </tr>
          </thead>
          <tbody>
            {(h.surfaces || []).map(s => (
              <tr key={s.surface} className={s.last_error ? 'text-red-700' : 'text-earth-800'}>
                <td className="py-1 pr-4 font-mono text-xs">{s.surface}{!s.enabled && ' (off)'}</td>
                <td className="py-1 pr-4 text-right">{Number(s.rows || 0).toLocaleString()}</td>
                <td className="py-1 pr-4 whitespace-nowrap">{s.last_pull_at ? `${formatEAT(s.last_pull_at)} (${s.last_pull_mode})` : '—'}</td>
                <td className="py-1 pr-4">{s.last_pull_at ? `${s.last_pull_adds ?? 0} / ${s.last_pull_changes ?? 0} / ${s.last_pull_deletes ?? 0}` : ''}</td>
                <td className="py-1 pr-4 text-xs">{s.last_error || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Runs */}
      <div className="card p-6 overflow-x-auto">
        <h3 className="text-lg font-medium text-primary-900 mb-3">Recent runs</h3>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-earth-500 border-b border-earth-200">
              <th className="py-2 pr-4">Started</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Data as of</th>
              <th className="py-2 pr-4 text-right">Requests</th><th className="py-2 pr-4 text-right">Rows</th>
              <th className="py-2 pr-4 text-right">Media</th><th className="py-2 pr-4 text-right">Promoted</th><th className="py-2 pr-4">Error</th>
            </tr>
          </thead>
          <tbody>
            {(h.runs || []).map(r => (
              <tr key={r.id} className={r.status === 'failed' ? 'text-red-700' : 'text-earth-800'}>
                <td className="py-1 pr-4 whitespace-nowrap">{formatEAT(r.started_at)}</td>
                <td className="py-1 pr-4">{r.status}{r.skip_reason ? ` (${r.skip_reason})` : ''}</td>
                <td className="py-1 pr-4 whitespace-nowrap">{r.data_as_of_utc ? formatEAT(r.data_as_of_utc) : ''}</td>
                <td className="py-1 pr-4 text-right">{r.requests}</td>
                <td className="py-1 pr-4 text-right">{r.rows_upserted}{r.rows_deleted ? ` / −${r.rows_deleted}` : ''}</td>
                <td className="py-1 pr-4 text-right">{r.media_downloaded}{r.media_failed ? ` (${r.media_failed} failed)` : ''}</td>
                <td className="py-1 pr-4 text-right">{r.events_promoted}{r.events_retracted ? ` / −${r.events_retracted}` : ''}</td>
                <td className="py-1 pr-4 text-xs">{r.error || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
